export interface NuveiRedirectUrls {
  redirectUrl: string;
  cancelUrl: string;
}

export type NuveiPaymentFlow = 'add-card' | 'direct-pay';

export interface NuveiPaymentContext {
  flow: NuveiPaymentFlow;
  tenantBuildingId?: string;
  tenantPaymentOptionId?: string;
  payPlan?: 'direct' | 'autopay';
  /** Direct payment settling a failed autopay payment; the pending retry is cancelled on 3DS success. */
  isManualRetry?: boolean;
  pendingIdvAuthHold?: boolean;
  /** Points previewed before payment; restored on 3DS return. */
  earnedPoints?: number;
}

const NUVEI_PAYMENT_CONTEXT_KEY = 'nuveiPaymentContext';

/** Backend openOrder requires tenantpay.com redirect URLs. */
export function getTenantPayAppOrigin(): string {
  const { origin, hostname } = window.location;
  if (hostname.endsWith('.tenantpay.com')) {
    return origin;
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'https://staging-app.tenantpay.com';
  }
  return 'https://app.tenantpay.com';
}

export function getNuveiRedirectUrls(): NuveiRedirectUrls {
  const baseOrigin = getTenantPayAppOrigin();
  return {
    redirectUrl: `${baseOrigin}/payment-success`,
    cancelUrl: `${baseOrigin}/payment-failure`,
  };
}

export function saveNuveiPaymentContext(context: NuveiPaymentContext): void {
  sessionStorage.setItem(NUVEI_PAYMENT_CONTEXT_KEY, JSON.stringify(context));
}

export function loadNuveiPaymentContext(): NuveiPaymentContext | null {
  try {
    const raw = sessionStorage.getItem(NUVEI_PAYMENT_CONTEXT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as NuveiPaymentContext;
  } catch {
    return null;
  }
}

export function clearNuveiPaymentContext(): void {
  sessionStorage.removeItem(NUVEI_PAYMENT_CONTEXT_KEY);
}

export type NuveiTransactionOutcome = 'approved' | 'declined' | 'pending' | 'cancelled' | 'error';

export function normalizeNuveiTransactionStatus(
  result: Record<string, unknown>
): NuveiTransactionOutcome {
  if (result.cancelled === true || result.cancelled === 'true') {
    return 'cancelled';
  }
  const status = String(
    result.transactionStatus ?? result.status ?? result.result ?? ''
  ).toLowerCase();
  if (status === 'approved') return 'approved';
  if (status === 'declined') return 'declined';
  if (status === 'pending' || status === 'redirect') return 'pending';
  return 'error';
}

export interface ErrorMapping {
  patterns: string[];
  message: string;
  addCardMessage?: string;
}

/**
 * Standardized mapping from processor/bank/Nuvei error responses to simple,
 * user-friendly English messages with actionable instructions.
 * Matches the backend error mapping patterns in error-mapping.config.ts and
 * adds specific guidance for 3DS / authentication / card verification issues.
 */
export const paymentErrorMessageMappings: ErrorMapping[] = [
  // 3D Secure / Identity / Bank Verification Failures (Nuvei 3DS, ACS, reason codes 01-24, errCode 9064)
  {
    patterns: [
      '3D-SECURE',
      '3DS',
      'CHALLENGE',
      'AUTHENTICATION FAILED',
      '3D SECURE',
      'CARD NUMBER, CURRENCY AND AMOUNT MUST BE EQUAL',
      'ACS',
      'IDENTITY VERIFICATION',
      '3DS_FAILED',
      'VERIFICATION FAILED',
      '3DS TIMEOUT',
      'NOT ENROLLED IN 3D',
      'DOES NOT SUPPORT 3D',
      'CARD IS NOT ENROLLED',
      '3D AUTHENTICATION IS NOT AVAILABLE',
      '9064',
      'ACS TECHNICAL ISSUE',
      'UNKNOWN DEVICE',
      'SECURITY FAILURE',
      'CHALLENGE CANCELLED',
      'CHALLENGE_CANCELLED',
      'AUTHENTICATION REJECTED',
      '3D_SECURE_FAILED',
      'AUTHENTICATION ERROR',
      'APM AUTHENTICATION ERROR',
      'CARD AUTHENTICATION FAILED',
    ],
    message:
      'The 3D Secure verification for this card could not be completed properly. Please ensure you approve the prompt in your banking app or SMS, or try using a different card.',
    addCardMessage:
      'The 3D Secure verification for this card could not be completed. Please approve the prompt in your banking app or SMS, or try a different card.',
  },
  // Address / AVS verification
  {
    patterns: [
      'AVS FAILED',
      'AVS MISMATCH',
      'ADDRESS VERIFICATION FAILED',
      'ZIP MISMATCH',
      'POSTAL CODE MISMATCH',
      'BILLING ADDRESS MISMATCH',
    ],
    message:
      'The billing address or postal code entered does not match your card issuer records. Please verify your billing details and try again.',
    addCardMessage:
      'The billing address or postal code entered does not match your card issuer records. Please verify your billing details and try again.',
  },
  // Card activation / status
  {
    patterns: [
      'ACCOUNT NOT YET ACTIVATED',
      'CARD NOT ACTIVATED',
      'INACTIVE CARD',
      'NOT ACTIVATED',
    ],
    message:
      'Your card has not been activated yet. Please activate your card with your bank or use a different card.',
    addCardMessage:
      'Your card has not been activated yet. Please activate your card with your bank or use a different card.',
  },
  {
    patterns: [
      'INSUFFICIENT FUNDS',
      'NSF - NOT SUFFICIENT FUNDS',
      'NSF',
      'NOT SUFFICIENT FUNDS',
      'EXCEEDS BALANCE',
    ],
    message:
      'Your payment could not be processed due to insufficient funds. Please ensure sufficient balance in your account and try again.',
    addCardMessage:
      'Your card could not be verified due to insufficient funds. Please ensure sufficient balance and try again.',
  },
  {
    patterns: [
      'EXCEEDS WITHDRAWAL LIMIT',
      'LIMIT EXCEEDED',
      'EXCEEDS LIMIT',
      'DAILY LIMIT',
      'ACTIVITY LIMIT EXCEEDED',
      'EXCEEDS AUTHENTICATION FREQUENCY',
      'MAXIMUM AMOUNT EXCEEDED',
      'OVER CREDIT LIMIT',
    ],
    message:
      'This payment exceeds your card or transaction limit. Please contact your bank to increase your limit or try a different payment method.',
    addCardMessage:
      'Verification exceeded your card transaction limit or frequency. Please contact your bank or try a different card.',
  },
  // Fraud, Risk Engine, Nuvei decline codes (1116, TRANSREC=D, TRANSREASON, TRANSSCORE)
  {
    patterns: [
      'SUSPECTED FRAUD',
      'FRAUD',
      'SECURITY VIOLATION',
      '1116',
      '1000001126',
      'TRANSREC=D',
      'TRANSREC',
      'TRANSREASON',
      'TRANSSCORE',
      'TRANSREASONAMOUNT',
      'RISK RULE',
      'RISK ENGINE',
      'BLOCK GENERAL',
      'RESTRICTED BY RISK',
    ],
    message:
      'Your payment was declined for security reasons by your bank or payment processor. Please contact your bank to authorize transactions, or use a different card.',
    addCardMessage:
      'This card could not be added for security reasons. Please try using a different card or contact your bank.',
  },
  {
    patterns: ['INVALID EXPIRATION DATE', 'EXPIRED CARD', 'EXPIRED', 'INVALID EXPIRY'],
    message:
      'Your card has expired or the expiry date entered is incorrect. Please check the expiry date or use a different card.',
    addCardMessage:
      'Your card has expired or the expiry date entered is incorrect. Please check the expiry date or use a different card.',
  },
  {
    patterns: ['INVALID CVV', 'INVALID CVC', 'CVV MISMATCH', 'CVC ERROR', 'SECURITY CODE'],
    message:
      'The security code (CVV) entered is incorrect. Please check the 3 or 4-digit code on your card and try again.',
    addCardMessage:
      'The security code (CVV) entered is incorrect. Please check the 3 or 4-digit code on your card and try again.',
  },
  {
    patterns: [
      'INVALID CARD NUMBER',
      'CARD NUMBER INVALID',
      'INVALID ACCOUNT NUMBER',
      'INVALID/INCORRECT A/C NUMBER',
      'INVALID CHECKSUM',
      'LUHN CHECK',
      'BAD CARD NUMBER',
    ],
    message:
      'The card number entered is invalid. Please check the card number and try again.',
    addCardMessage:
      'The card number entered is invalid. Please check the card number and try again.',
  },
  {
    patterns: [
      'DUPLICATE TRANSACTION',
      'ALREADY PROCESSED',
      'ALREADY REVERSED',
      'TRANSACTION ALREADY PROCESSED',
    ],
    message:
      'A transaction with these details was already submitted recently. Please check your payment history before trying again.',
    addCardMessage:
      'This card was already submitted recently. Please wait a moment before trying again.',
  },
  {
    patterns: [
      'CURRENCY NOT SUPPORTED',
      '9065',
      'CURRENCY MISMATCH',
      'AMOUNT MISMATCH',
      'PAYMENT METHOD NOT ALLOWED',
    ],
    message:
      'This currency or payment method is not supported by your card. Please try a different card or payment method.',
    addCardMessage:
      'This currency or card type is not supported. Please try a different card.',
  },
  {
    patterns: ['MANDATORY FIELDS ARE MISSING', 'MISSING FIELD', 'INVALID PARAMETERS'],
    message:
      'Some required payment details are missing or invalid. Please re-enter your payment information and try again.',
    addCardMessage:
      'Some required card details are missing or invalid. Please re-enter your details and try again.',
  },
  {
    patterns: ['PICK-UP CARD', 'PICK UP CARD', 'LOST CARD', 'STOLEN CARD'],
    message:
      'Your card has been reported lost or restricted by your bank. Please use a different card or contact your bank.',
    addCardMessage:
      'Your card has been reported lost or restricted by your bank. Please use a different card.',
  },
  {
    patterns: [
      'BLOCKED BY CARDHOLDER',
      'RESTRICTED CARD',
      'CARD BLOCKED',
      'RESTRICTED',
      'CARD NOT ALLOWED',
      'ONLINE PAYMENTS DISABLED',
    ],
    message:
      'This card has been blocked or restricted by your bank. Please contact your bank to unblock it or use a different card.',
    addCardMessage:
      'This card has been blocked or restricted by your bank. Please contact your bank to unblock it or use a different card.',
  },
  {
    patterns: [
      'TRANSACTION NOT PERMITTED',
      'TRANSACTION NOT SUPPORTED',
      'DOMESTIC DEBIT TRANSACTION NOT ALLOWED',
      'NOT PERMITTED TO CARDHOLDER',
      'NOT ALLOWED',
    ],
    message:
      'Your bank does not support this type of transaction on this card. Please use a different card or payment method.',
    addCardMessage:
      'Your bank does not support online transactions or recurring payments on this card. Please use a different card.',
  },
  {
    patterns: ['CLOSED ACCOUNT', 'NO CURRENT ACCOUNT', 'ACCOUNT NOT FOUND'],
    message:
      'The bank account linked to this payment is closed or could not be found. Please check your account details or use a different card.',
    addCardMessage:
      'The account linked to this payment could not be found. Please check your details or use a different card.',
  },
  {
    patterns: ['IDENTITY VERIFICATION WAS NOT COMPLETED'],
    message: 'Identity verification was not completed within the hold period.',
    addCardMessage: 'Identity verification was not completed within the hold period.',
  },
  {
    patterns: [
      'NOT IN ACCORD WITH AGREE',
      'NO AGREEMENT EXISTED',
      'AGREEMENT REVOKED',
    ],
    message:
      'There is an issue with the pre-authorized debit (PAD) agreement on your bank account. Please contact your bank or try a different payment method.',
  },
  {
    patterns: ['REVOCATION OF AUTHORIZATION'],
    message:
      'The authorization for this payment was revoked by your bank. Please contact your bank or use a different payment method.',
  },
  {
    patterns: ['STOP RETRIES'],
    message:
      'Your payment could not be processed. Please try again after some time or use a different payment method.',
  },
  {
    patterns: ['MODULUS CHECK', 'POSITIONAL ERROR'],
    message:
      'The account details entered failed the bank validation check. Please verify your information and try again.',
  },
  {
    patterns: ['TRANSIT NUMBER NOT FOUND'],
    message:
      'The transit number entered could not be found at your financial institution. Please check your transit number and try again.',
  },
  {
    patterns: ['BANK NUMBER NOT FOUND'],
    message:
      'The institution (bank) number entered could not be found. Please check your bank details and try again.',
  },
  {
    patterns: ['FUNDS NOT CLEARED'],
    message:
      'The funds for this payment have not cleared yet. Please try again after some time.',
  },
  {
    patterns: ['INVALID MERCHANT'],
    message:
      'This payment could not be processed. Please try again after some time or contact support.',
  },
  {
    patterns: [
      'TIMEOUT/RETRY',
      'TIMEOUT',
      'FILE TEMPORARILY NOT AVAILABLE',
      'UNEXPECTED SYSTEM ERROR',
      'COMMUNICATION ERROR',
      'GATEWAY ERROR',
      'SERVER ERROR',
      'ACQUIRER VALIDATION',
      'GATEWAY TIMEOUT',
    ],
    message:
      'A temporary communication error occurred while processing your request. Please try again in a few moments.',
    addCardMessage:
      'A temporary communication error occurred while verifying your card. Please try again in a few moments.',
  },
  {
    patterns: ['PAYMENT CANCELLED BY USER', 'USER CANCELLED'],
    message:
      'Payment was cancelled. You can try again whenever you are ready.',
    addCardMessage:
      'Card verification was cancelled. You can try again whenever you are ready.',
  },
  {
    patterns: ['PAYMENT CANCELLED BY ADMINISTRATOR'],
    message:
      'This payment was cancelled by an administrator. Please contact support if you believe this is a mistake.',
  },
  {
    patterns: ['NO CONF/PRENOTIFICATION PER'],
    message:
      'Your bank account is still within its mandatory pre-notification waiting period and cannot be debited yet. Please try again in a few days.',
  },
  {
    patterns: ['AUTH CODE/TRANS ID/CREDIT CARD NUMBER MISMATCH'],
    message:
      'This refund could not be matched to the original transaction. Please verify the transaction and try again.',
  },
  {
    patterns: ['LIFE CYCLE'],
    message:
      'This card cannot be used for this transaction due to its current status. Please contact your bank or use a different card.',
    addCardMessage:
      'This card cannot be used due to its current status. Please contact your bank or use a different card.',
  },
  // Generic decline fallback
  {
    patterns: ['DO NOT HONOR', 'GENERIC DECLINE', 'DECLINED', 'DECLINE'],
    message:
      'Your payment was declined by your bank. Please check your account details or contact your bank for more information.',
    addCardMessage:
      'Your card was declined by your bank. Please check your card details or use a different card.',
  },
];

/** Extracts raw technical error string from Nuvei/processor response for logging. */
export function getNuveiRawErrorDescription(
  result: Record<string, unknown> | string | null | undefined
): string {
  if (!result) return '';
  if (typeof result === 'string') return result.trim();
  const gwErrorReason = result.gwErrorReason;
  const parts = [
    result.errorDescription,
    result.reason,
    result.message,
    result.threeDReason,
    typeof gwErrorReason === 'string' ? gwErrorReason : '',
    result.errCode != null ? `Error code: ${result.errCode}` : '',
    result.gwErrorCode != null ? `GW Error code: ${result.gwErrorCode}` : '',
  ].filter(Boolean);
  return parts.map(String).join('\n').trim();
}

/** Maps raw processor/Nuvei error string into a simple, actionable English message. */
export function mapNuveiErrorMessage(
  rawError: string | null | undefined,
  flow: NuveiPaymentFlow = 'direct-pay'
): string {
  if (!rawError || !rawError.trim()) {
    return flow === 'add-card'
      ? 'Unable to verify and add this card. Please check your card details or try a different card.'
      : 'Your payment could not be processed. Please check your payment details or try a different payment method.';
  }

  const normalized = rawError.toUpperCase();
  for (const { patterns, message, addCardMessage } of paymentErrorMessageMappings) {
    if (patterns.some((p) => normalized.includes(p))) {
      if (flow === 'add-card' && addCardMessage) {
        return addCardMessage;
      }
      return message;
    }
  }

  return flow === 'add-card'
    ? 'Unable to verify and add this card. Please check your card details or try a different card.'
    : 'Your payment could not be processed. Please check your payment details or try a different payment method.';
}

/** Returns a simple, user-friendly English error message with clear action items. */
export function getNuveiUserFacingErrorMessage(
  result: Record<string, unknown> | string | null | undefined,
  flow: NuveiPaymentFlow = 'direct-pay'
): string {
  const raw = typeof result === 'string' ? result : getNuveiRawErrorDescription(result);
  return mapNuveiErrorMessage(raw, flow);
}

/**
 * Returns user-friendly error message for payment outcomes.
 * Backwards compatible with existing call sites.
 */
export function getNuveiErrorDescription(result: Record<string, unknown>): string {
  return getNuveiUserFacingErrorMessage(result, 'direct-pay');
}

export interface NuveiBillingAddress {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  city?: string;
  zip?: string;
  state?: string;
  address?: string;
}

function splitCardHolderName(cardHolderName: string): { firstName: string; lastName: string } {
  const nameParts = cardHolderName.trim().split(/\s+/).filter(Boolean);
  const firstName = nameParts[0] ?? 'Cardholder';
  const lastName = nameParts.slice(1).join(' ') || firstName;
  return { firstName, lastName };
}

/** Billing address fields required for 3DS issuer authentication during add-card. */
export function buildAddCardBillingAddress(
  values: {
    cardHolderName: string;
    address: string;
    unitNumber?: string;
    house?: string;
    street?: string;
    city: string;
    postal: string;
    provinceCode: string;
    country: string;
  },
  email: string
): NuveiBillingAddress {
  const streetLine = [values.house, values.street].filter(Boolean).join(' ').trim();
  const rawStreet =
    streetLine || values.address?.split(',')[0]?.trim() || values.address?.trim() || '';
  const streetAddress = [values.unitNumber, rawStreet].filter(Boolean).join(', ').trim();
  const { firstName, lastName } = splitCardHolderName(values.cardHolderName);

  return {
    firstName,
    lastName,
    email,
    country: (values.country || 'CA').toUpperCase(),
    city: values.city,
    zip: values.postal,
    state: values.provinceCode,
    address: streetAddress || values.address,
  };
}

function getNuveiThreeD(result: Record<string, unknown>): Record<string, unknown> | undefined {
  const paymentOption = result.paymentOption as Record<string, unknown> | undefined;
  const card = paymentOption?.card as Record<string, unknown> | undefined;
  return (card?.threeD ?? paymentOption?.threeD) as Record<string, unknown> | undefined;
}

export function getNuvei3dsEci(result: Record<string, unknown>): string {
  const threeD = getNuveiThreeD(result);
  const card = (result.paymentOption as Record<string, unknown> | undefined)?.card as
    | Record<string, unknown>
    | undefined;
  return String(result.eci ?? threeD?.eci ?? card?.eci ?? '');
}

/** Normalize Nuvei ECI values like "05" / "02" to "5" / "2". */
export function normalizeNuvei3dsEci(eci: string): string {
  const trimmed = eci.trim();
  if (!trimmed) return '';
  const parsed = Number.parseInt(trimmed, 10);
  if (Number.isNaN(parsed)) return '';
  return String(parsed);
}

export function getNuvei3dsCavv(result: Record<string, unknown>): string {
  const threeD = getNuveiThreeD(result);
  const card = (result.paymentOption as Record<string, unknown> | undefined)?.card as
    | Record<string, unknown>
    | undefined;
  return String(result.cavv ?? threeD?.cavv ?? card?.cavv ?? '').trim();
}

function getNuveiDsTransId(result: Record<string, unknown>): string {
  const threeD = getNuveiThreeD(result);
  return String(result.dsTransID ?? result.dsTransId ?? threeD?.dsTransID ?? '').trim();
}

function isNuveiLiabilityOnIssuer(result: Record<string, unknown>): boolean {
  const value = result.isLiabilityOnIssuer;
  return value === true || value === 1 || value === '1';
}

/** Nuvei 3DS flow from createPayment: "challenge" | "frictionless" | "". */
export function getNuvei3dsFlow(result: Record<string, unknown>): string {
  const nestedFlow = String(getNuveiThreeD(result)?.flow ?? '').trim().toLowerCase();
  if (nestedFlow) {
    return nestedFlow;
  }

  // WebSDK createPayment flattens 3DS fields onto the top-level callback.
  if (getNuvei3dsCavv(result) || getNuveiDsTransId(result)) {
    return 'frictionless';
  }
  if (isNuveiLiabilityOnIssuer(result)) {
    return 'challenge';
  }
  return '';
}

/**
 * True when 3DS was performed during createPayment.
 * WebSDK callbacks are flattened (no paymentOption.card.threeD.flow), so also use:
 * challenge UI, CAVV, authenticated ECI (2/5), dsTransID, or issuer liability shift.
 */
export function isNuveiPayment3ds(
  result: Record<string, unknown>,
  challengePresented = false
): boolean {
  if (challengePresented) {
    return true;
  }

  const flow = getNuvei3dsFlow(result);
  if (flow === 'challenge' || flow === 'frictionless') {
    return true;
  }

  const eci = normalizeNuvei3dsEci(getNuvei3dsEci(result));
  return eci === '2' || eci === '5';
}

export type Nuvei3dsUnsupportedReason = 'eci_non_participation' | 'issuer_not_enrolled';

/** Why the result indicates the card cannot use 3DS; null means 3DS was not ruled out. */
export function getNuveiCard3dsUnsupportedReason(
  result: Record<string, unknown>
): Nuvei3dsUnsupportedReason | null {
  const eci = normalizeNuvei3dsEci(getNuvei3dsEci(result));
  const outcome = normalizeNuveiTransactionStatus(result);

  // Nuvei documents these ECIs only for issuer/cardholder non-participation on a completed auth attempt.
  if (outcome === 'approved' && (eci === '1' || eci === '6')) {
    return 'eci_non_participation';
  }

  const errorText = `${getNuveiErrorDescription(result)} ${String(result.threeDReason ?? '')}`.toLowerCase();
  const notEnrolledPhrases = [
    'does not support 3d',
    '3d secure is not supported',
    '3ds not supported',
    'not enrolled in 3d',
    'card is not enrolled',
    '3d authentication is not available',
  ];

  if (notEnrolledPhrases.some((phrase) => errorText.includes(phrase))) {
    return 'issuer_not_enrolled';
  }

  return null;
}

/** True when the card/issuer does not support 3DS. */
export function isNuveiCard3dsUnsupported(result: Record<string, unknown>): boolean {
  return getNuveiCard3dsUnsupportedReason(result) !== null;
}

const NUVEI_3DS_CHALLENGE_ATTR = 'data-nuvei-3ds-challenge';

/** Nuvei process-event handlers — hide app modal when 3DS challenge UI opens. */
export function createNuvei3dsEventHandlers(): {
  onPaymentEvent: (result: Record<string, unknown>) => void;
  clearNuvei3dsChallengeState: () => void;
  wasChallengePresented: () => boolean;
} {
  let challengePresented = false;

  const setChallengeActive = (active: boolean): void => {
    if (active) {
      document.body.setAttribute(NUVEI_3DS_CHALLENGE_ATTR, 'true');
    } else {
      document.body.removeAttribute(NUVEI_3DS_CHALLENGE_ATTR);
    }
  };

  return {
    onPaymentEvent: (result) => {
      const eventType = String(result.eventType ?? '');
      if (eventType === 'challengeStarted') {
        challengePresented = true;
        setChallengeActive(true);
      }
      if (eventType === 'challengeEnded') {
        setChallengeActive(false);
      }
    },
    clearNuvei3dsChallengeState: () => setChallengeActive(false),
    wasChallengePresented: () => challengePresented,
  };
}
